import { APIResponse } from 'types/Request';
import { getCookie } from 'helpers';
import config from 'config';
import { Insurer } from 'features/insurers/insurersAPI';
import { getErrorMessage } from 'helpers/errors';
import { HonorariosEntidades } from 'types/MatriculateRequest';

export async function changePassword(newPassword: string): Promise<boolean> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/change-password`);

      type Response = APIResponse<undefined>

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('accessToken'),
        },
        body: JSON.stringify({
          password: newPassword,
        }),
      });

      if (response.status !== 200) {
        return false;
      }

      const content = await response.json() as Response;

      return content.status === 'ok';
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar cambiar la contraseña. ');
  }
}

export interface InsurerWithState extends Insurer {
  enabled: boolean;
}

export async function getMatriculateInsurers(): Promise<InsurerWithState[]> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/insurers`);

      type Response = APIResponse<InsurerWithState[]>

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('accessToken'),
        },
      });

      if (response.status !== 200) {
        throw new Error('Ocurrió un error inesperado al recuperar las obras sociales');
      }

      const content = await response.json() as Response;

      if (content.status !== 'ok' || !content.payload) {
        throw new Error('Ocurrió un error inesperado al recuperar las obras sociales');
      }

      return content.payload;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateMatriculateInsurers(insurerId: number, enabled: boolean): Promise<boolean> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/insurers`);

      type Response = APIResponse<undefined>

      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('accessToken'),
        },
        body: JSON.stringify({
          insurer_id: insurerId,
          enabled,
        }),
      });

      if (response.status !== 200) {
        return false;
      }

      const content = await response.json() as Response;

      return content.status === 'ok';
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al actualizar la integración con la obra social. ');
  }
}

export interface AccountData {
  nombre: string
  apellido: string
  tipo_doc: string
  nro_doc: string
  matricula: string
  email: string
  celular_codigo: string
  celular_numero: string
  domicilio_particular: Domicilio
  domicilio_legal: Domicilio
  especialidades: Especialidad[]
}

interface Domicilio {
  calle: string
  numero: string
  piso: string
  depto: string
  cp: string
  localidad_id: string
  localidad: string
  partido_id: string
  partido: string
  telefono_codigo: string
  telefono_numero: string
}

interface Especialidad {
  id: number;
  descripcion: string;
}

export async function getAccountData(): Promise<AccountData> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/account`);

    type Response = APIResponse<AccountData>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) || 'Ocurrió un problema inesperado al obtener los datos del matriculado.' );
  }
}

type UpdateAccountData = Partial<Omit<AccountData, 'especialidades'>>

export async function updateAccountData(data: UpdateAccountData): Promise<number> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/account`);

    type Response = APIResponse<number>

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
      body: JSON.stringify(data),
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) ||
      'Ocurrió un problema inesperado al actualizar los datos del matriculado.' );
  }
}

export interface AccountRequestSummary {
  id: number;
  state_id: number;
  created_at: string | null;
  resolved_at: string | null;
  rejection_text: string | null;
}

export async function getMyAccountRequests(
  page = 1,
  limit = 10,
): Promise<{ rows: AccountRequestSummary[]; count: number }> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/account/requests`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('limit', String(limit));

    type Response = APIResponse<{ rows: AccountRequestSummary[]; count: number }>

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (res.status !== 200) {
      throw new Error();
    }

    const content = await res.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error(getErrorMessage(err) || 'No se pudieron recuperar los pedidos de cambio de datos.');
  }
}

export interface AccountRequestDetailData extends AccountRequestSummary {
  email: string | null;
  celular_codigo: string | null;
  celular_numero: string | null;
  particular_calle: string | null;
  particular_numero: number | null;
  particular_piso: string | null;
  particular_depto: string | null;
  particular_cp: string | null;
  particular_localidad: string | null;
  particular_partido: string | null;
  particular_telefono_codigo: string | null;
  particular_telefono_numero: string | null;
  legal_calle: string | null;
  legal_numero: number | null;
  legal_piso: string | null;
  legal_depto: string | null;
  legal_cp: string | null;
  legal_localidad: string | null;
  legal_partido: string | null;
  legal_telefono_codigo: string | null;
  legal_telefono_numero: string | null;
}

export async function getMyAccountRequestById(id: number | string): Promise<{
  new: AccountRequestDetailData | null;
  old: AccountRequestDetailData | null;
}> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/account/${ id }`);

    type Response = APIResponse<{
      new: AccountRequestDetailData | null;
      old: AccountRequestDetailData | null;
    }>

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (res.status !== 200) {
      throw new Error();
    }

    const content = await res.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error(getErrorMessage(err) || 'No se pudo recuperar el pedido de cambio de datos.');
  }
}

export interface Cuota {
  matricula: number
  anio: string
  concepto: string
  cuota: number
  recargo: number
  pagado: number
  fechaDePago: string
  convenio: boolean
}

export async function getHistóricoCuotas(): Promise<Cuota[]> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/cuotas/historico`);

    type Response = APIResponse<Cuota[]>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) ||
      'Ocurrió un problema inesperado al recuperar el histórico de cuotas.' );
  }
}

export interface Debt {
  opcion: number
  formaPago: string
  monto: number
  concepto: string
  anio: number
  recargo: number
  convenio: boolean
}

export async function getCuotasPendientes(matriculateId: number): Promise<Debt[]> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/${ matriculateId }/cuotas-pendientes`);

    type Response = APIResponse<Debt[]>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) ||
      'Ocurrió un problema inesperado al recuperar las cuotas pendientes.' );
  }
}

interface MatriculateInfo {
  matricula: number;
  nombre: string;
}

export async function getMatriculateInfo(matriculateId: number): Promise<MatriculateInfo> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/${ matriculateId }/info`);

    type Response = APIResponse<MatriculateInfo>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) ||
      'Ha ocurrido un error obteniendo la información del usuario.' );
  }
}

export async function getHonorariosEntidades(titulosPagos: string[]): Promise<HonorariosEntidades[]> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/request/honorarios`);

    type Response = APIResponse<HonorariosEntidades[]>

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
      body: JSON.stringify({
        titulosPagos,
      }),
    });

    if (response.status !== 200) {
      throw new Error();
    }

    const content = await response.json() as Response;

    if (!content.payload) {
      throw new Error();
    }

    return content.payload;
  } catch (err) {
    throw new Error( getErrorMessage(err) ||
      'Ha ocurrido un error obteniendo la información del usuario.' );
  }
}
